import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "@/lib/dates";
import {
  type BundleTarget,
  type OutboxAttachmentRow,
  type OutboxSubmissionRow,
  type StoredAssignment,
  type StoredDraft,
} from "@njbsoft/field-sync";
import { Card, EmptyState, PageSkeleton } from "@/components/ui";
import { SyncStatusChip, outboxRowRequests } from "@/components/tester/chrome/sync-status-chip";
import { pendingRequestKind } from "@/components/tester/work/assignment-card";
import { outboxRowProposals } from "@/components/tester/execution/violation-proposals-cure";
import { isKitRequirement } from "@/components/tester/start-test/start-test-rows";
import { readinessTone } from "@/components/tester/start-test/readiness-gate";
import { StickyActionBar } from "@/components/tester/work-detail/sticky-action-bar";
import {
  pendingChangeRequestCount,
  SummaryChipStrip,
} from "@/components/tester/work-detail/summary-chip-strip";
import { CureBanner } from "@/components/tester/work-detail/cure-banner";
import { BeforeYouStartCard } from "@/components/tester/work-detail/before-you-start-card";
import { TargetCard } from "@/components/tester/work-detail/target-card";
import { useFieldworkStorage } from "@/lib/fieldwork/storage-provider";
import { todayIso } from "@/lib/requirement-treatment";
import { useMyWorkAssignment } from "@/hooks/use-my-work-assignment";
import { useMyTestKits } from "@/hooks/use-my-test-kits";
import { useAssignmentReadiness } from "@/hooks/use-assignment-readiness";
import { AssignmentThreadsCard } from "@/components/tester/work/assignment-threads-card";
import { useOfflineStartGate } from "@/hooks/use-offline-start-gate";
import { useStartAndOpen } from "@/hooks/use-start-and-open";
import { useChangeRequestOutbox } from "@/hooks/use-change-request-outbox";
import { useMySubmissions } from "@/hooks/use-my-submissions";
import { useMySubmission } from "@/hooks/use-my-submission";
import { type components } from "@njbsoft/types/field";

type WorkAssignment = components["schemas"]["WorkAssignment"];

export const Route = createFileRoute("/_company/my-work/$assignmentId/")({
  component: TesterAssignmentDetailPage,
});

/**
 * Whether a stored draft holds work worth returning to: a locked draft is
 * sealed (the enqueue transaction locks it) and an empty one is nothing to
 * resume.
 */
export function hasDraftInProgress(draft: StoredDraft | null): boolean {
  return draft !== null && !draft.locked && Object.keys(draft.answers).length > 0;
}

/** The gate's subject line — the premise the start is decided about. */
function gateSubject(target: BundleTarget | null, effective: WorkAssignment): ReactNode {
  return (
    <span className="font-semibold">
      {target?.facility_name ?? effective.facility_name ?? effective.org_name ?? ""}
    </span>
  );
}

/**
 * Work-assignment detail, dual-source: the field-stack row when the network has
 * it, the locally synced bundle row otherwise, so the screen keeps working with
 * no signal. Laid out to the locked work-detail contract: a sticky bar pinning
 * ‹ Work, the title, the status, Threads and the ONE forward action — Start &
 * open — above the fold; a one-line chip summary; the cure banner above
 * everything; then the condensed Before-you-start, Target and Threads cards.
 *
 * Start & open transitions the item to `in_progress` AND opens the form,
 * because the separate Start Work / Open Form pair was two clicks expressing
 * one intention and a tester who took only the first one left the item parked.
 * Its enable/hold semantics are the readiness gate's: the button holds while
 * verdicts load or refuse, and the gate in the "Before you start" card stays
 * the verdict surface — its action renders in the sticky bar under a shared
 * acknowledgement, so the sentences and the button can never disagree. A gauge
 * is picked here for the same reason the start picker picks one — readiness is
 * kit-anchored. With no gauge or a non-device target the action stands ungated
 * rather than unreachable: the pre-flight is advisory and the receive path is
 * the real gate, and an offline-first screen must not require a round trip to
 * start work. OFFLINE the same gate consults the STORED block instead — the
 * last pull's requirements re-checked against today, because no report can be
 * fetched and no server will refuse the transition until replay; a stored
 * block the bundle never carried is an open gate, never a stranded tester.
 */
function TesterAssignmentDetailPage() {
  const { t } = useTranslation(["field-work", "common"]);
  const { assignmentId } = Route.useParams();
  const { storage, engine } = useFieldworkStorage();

  const { data: assignment, isLoading, isError } = useMyWorkAssignment(assignmentId);
  // The device's change-request state, through the SAME source the My Work card
  // reads — so the pending chip and line below say exactly what the list row
  // says.
  const changeRequests = useChangeRequestOutbox();
  const [pickedKitId, setPickedKitId] = useState<string | null>(null);
  // The gate's acknowledgement, shared between the checkbox in the
  // "Before you start" card and the Start & open button in the sticky bar: the
  // gate owns the re-arm-on-new-warning rule; this state is what the two
  // surfaces read so they can never disagree.
  const [acknowledged, setAcknowledged] = useState(false);

  // This assignment's UNANSWERED rejection, derived client-side from the same
  // archive read the History pages use: the list narrows to rejected rows the
  // chain predicate has not answered (`unresolved`), and the assignment match
  // is the client's — no per-assignment wire filter exists, and none is added.
  // The moment a resubmission lands, the row leaves the server's set, the
  // detail read turns off, and the banner above disappears: the screen reads as
  // the resumed assignment with nothing still owed.
  const rejectionsQuery = useMySubmissions({
    status: "rejected",
    unresolved: true,
    sort_by: "performed_at",
    sort_dir: "desc",
  });
  const unresolvedRejection =
    (rejectionsQuery.data?.items ?? []).find((row) => row.assignment_id === assignmentId) ?? null;
  // The rejection's DETAIL is what the cure path consumes: `change_requests`
  // rides the detail alone, so pre-staging from the row would silently drop
  // the returned request the resubmission must stage again. The banner
  // therefore renders only once this read lands; while it is in flight the
  // screen shows everything else it already does, and if it fails the cure
  // stays reachable through the History detail and the Home banner, which
  // count the same row.
  const rejectionDetailQuery = useMySubmission(unresolvedRejection?.id ?? null);
  const rejectionDetail = unresolvedRejection === null ? null : (rejectionDetailQuery.data ?? null);

  const [bundleRow, setBundleRow] = useState<StoredAssignment | null>(null);
  const [outboxRows, setOutboxRows] = useState<OutboxSubmissionRow[]>([]);
  const [rowAttachments, setRowAttachments] = useState<Record<string, OutboxAttachmentRow[]>>({});
  const [draft, setDraft] = useState<StoredDraft | null>(null);
  const [target, setTarget] = useState<BundleTarget | null>(null);
  const [localLoaded, setLocalLoaded] = useState(false);

  const refreshLocal = useCallback(async () => {
    // No store to read is a SETTLED read, not a pending one. A session with no
    // tester arm never opens one, so leaving `localLoaded` false here holds the
    // skeleton up forever with no terminal state behind it — the screen has to
    // be able to say "nothing here" once the network read also settles.
    if (storage === null) {
      setLocalLoaded(true);
      return;
    }
    const [row, submissions, draftRow] = await Promise.all([
      storage.getBundleAssignment(assignmentId),
      storage.listForAssignment(assignmentId),
      storage.getDraft(assignmentId),
    ]);
    // A row carrying change requests counts its photos off these.
    const attachmentLists = await Promise.all(
      submissions.map((submission) =>
        storage.listAttachmentsForSubmission(submission.clientSubmissionId),
      ),
    );
    setBundleRow(row);
    setOutboxRows(submissions);
    setRowAttachments(
      Object.fromEntries(
        submissions.map((submission, i) => [
          submission.clientSubmissionId,
          attachmentLists[i] ?? [],
        ]),
      ),
    );
    setDraft(draftRow);
    setLocalLoaded(true);
  }, [storage, assignmentId]);

  // Mobile re-reads on screen focus; the browser substitution is a mount read
  // plus a visibility listener, so returning to a backgrounded tab picks up
  // whatever the sync engine wrote while it was away.
  //
  // Deliberately NOT gated on the provider's readiness: `refreshLocal` closes
  // over `storage`, so it re-runs on its own when the store opens, and gating
  // here would mean a provider that never opens one never settles the read.
  useEffect(() => {
    void refreshLocal();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void refreshLocal();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refreshLocal]);

  // A drain triggered anywhere else -- the queue's sync hook, a sibling tab,
  // the launch run -- lands submissions this screen is displaying, so without
  // an edge subscription the chip sits on "Queued" with a Retry button over
  // work the server already has. Only the FALLING edge re-reads: the rising
  // edge fires before the run moved anything. `wasRunning` rides a ref for the
  // same reason `wasOnline` does in the sync hook -- the subscribe callback
  // fires on both edges and the prior value is what distinguishes them.
  // Single-flight stays the engine's job; this registers no run trigger.
  const wasSyncRunning = useRef(false);
  useEffect(() => {
    if (engine === null) return;
    wasSyncRunning.current = engine.isSyncRunning();
    return engine.subscribeSyncState(() => {
      const nowRunning = engine.isSyncRunning();
      const settled = wasSyncRunning.current && !nowRunning;
      wasSyncRunning.current = nowRunning;
      if (settled) void refreshLocal();
    });
  }, [engine, refreshLocal]);

  // The network row wins when present; the synced bundle row keeps the screen
  // working offline.
  const effective = assignment ?? bundleRow?.assignment;
  const facilityId = effective?.facility_id;
  const installationId = effective?.device_installation_id ?? null;

  // The offline half of the start gate: the utility's requirements as the last
  // pull stored them, re-checked against today. Null with a connection — there
  // the server's own evaluation at the transition is the authority — and null
  // for a utility the bundle carried no block for, which is an OPEN gate. It is
  // consulted in the arm where the transition would QUEUE, because a cached
  // report's verdicts do NOT expire their own dates (the server is expected to
  // refuse at the transition, and offline there is no server until replay).
  const { decision: offlineStart, isLoading: offlineStartUnread } = useOfflineStartGate(
    effective?.org_id,
  );

  useEffect(() => {
    if (storage === null || facilityId === undefined) return;
    let cancelled = false;
    void storage.getTarget(facilityId, installationId).then((row) => {
      if (!cancelled) setTarget(row);
    });
    return () => {
      cancelled = true;
    };
  }, [storage, facilityId, installationId]);

  // Tap-to-retry on a chip first returns a `blocked` row to the queue —
  // `retryBlocked` is the only way back from that state, and syncing alone
  // would strand it — then kicks a FRESH sync run (one already in flight may
  // have drained before the row came back) and re-reads the local rows so the
  // chip reflects the run's outcome. A chip's cure lands here too once its
  // write has put the row back, which makes the first step a no-op. The engine
  // handle is taken straight off the provider rather than through the sync-bar
  // hook: this screen renders no bar and must not register a second set of run
  // triggers.
  const handleRetry = useCallback(
    async (row: OutboxSubmissionRow) => {
      if (storage !== null && row.state === "blocked") {
        await storage.retryBlocked(row.clientSubmissionId);
      }
      if (engine === null) return;
      await engine.syncFresh().then(() => refreshLocal());
    },
    [storage, engine, refreshLocal],
  );

  // The pre-flight is kit-anchored and only a device test carries a gauge, so
  // a premise inspection selects none and is started ungated.
  const isDeviceTest = effective?.target_kind === "device_test";
  const kitsQuery = useMyTestKits();
  const kits = kitsQuery.data?.items ?? [];
  const selectedKitId = isDeviceTest ? (pickedKitId ?? kits[0]?.id ?? null) : null;
  const readiness = useAssignmentReadiness(
    selectedKitId === null ? null : assignmentId,
    selectedKitId,
  );
  const startAndOpen = useStartAndOpen();
  // The assignment mode answers with exactly one row; its requirements are
  // this item's complete verdict list, the four gauge-judging ones included.
  const verdicts = readiness.data?.assignments?.[0]?.requirements ?? [];
  const kitVerdicts = verdicts.filter(isKitRequirement);
  // ONE calendar day for the gate's expiring window, read once per mount.
  const today = todayIso();

  // The Threads action jumps to the conversations card — the bar's action is a
  // place on THIS page, not a navigation. jsdom has no scrollIntoView; the
  // optional call keeps the unit surface honest about that. The card's top
  // margin clears the pinned bar it lands under: 62 px below sm; from sm the
  // title's flex basis holds the bar to two 44 px rows (128 px), whatever the
  // facility name's length.
  const threadsRef = useRef<HTMLDivElement>(null);
  const handleThreads = useCallback(() => {
    threadsRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, []);

  if (!effective) {
    if (isLoading || !localLoaded) {
      return (
        <div className="p-4 sm:p-8">
          <PageSkeleton />
        </div>
      );
    }
    // An assignment not visible to the caller surfaces as 404 by RLS policy;
    // with no bundle row either, there is nothing to render.
    return (
      <div className="p-4 sm:p-8">
        <EmptyState title={t("common:errors.somethingWentWrong")} />
      </div>
    );
  }

  // The optimistic local status (a transition queued offline) wins over the
  // last-fetched server status until the next pull restores server truth.
  const displayStatus = bundleRow?.localStatus ?? effective.status;
  // The receipt's gate: the test is in the utility's hands (`submitted`) or
  // back from them (`completed`). The status gate is load-bearing, not the
  // card it lives in — a fresh claim reaches this screen before the first
  // fieldwork pull writes a target row, so an OPEN assignment can render the
  // same fallback card and must not read as a receipt.
  const isReceiptStatus = displayStatus === "submitted" || displayStatus === "completed";
  // The same writable pair `isExecutionSealed` reads, so a tester who cannot
  // reach the form through this button cannot reach a writable one by deep
  // link either.
  const canOpenForm = displayStatus === "assigned" || displayStatus === "in_progress";
  const handleStart = () => {
    startAndOpen.start({
      assignmentId: effective.id,
      orgId: effective.org_id,
      kitId: selectedKitId,
    });
  };

  // The gate's decision over this item, derived through the SAME helper the
  // mounted gate renders — the sticky bar's button holds on it, so the
  // sentences below and the button above can never disagree. The
  // acknowledgement is the gate checkbox's own state, shared up so a warn the
  // tester has read arms the bar's button the moment it arms the gate's.
  const refusal =
    startAndOpen.refusal !== null && startAndOpen.refusal.assignmentId === assignmentId
      ? startAndOpen.refusal.failing
      : null;
  const gateMounted = verdicts.length > 0 || refusal !== null || offlineStart !== null;
  const gateTone = readinessTone({ verdicts, refusal, offline: offlineStart, today });
  const startHeld =
    readiness.isLoading ||
    offlineStartUnread ||
    gateTone === "block" ||
    (gateTone === "warn" && !acknowledged);

  const showStaleNote = isError && assignment === undefined;
  // The pending-set context the My Work card shows for this device: same data
  // source, same derivation — the chip counts it, the line names it.
  const pendingKind = pendingRequestKind(effective, changeRequests);
  const hasRevoked = outboxRows.some((row) => row.state === "revoked");

  return (
    <div className="space-y-4 p-4 sm:p-8">
      <StickyActionBar
        backTo="/my-work"
        title={
          target?.facility_name ??
          effective.facility_name ??
          effective.org_name ??
          t("field-work:detail.title")
        }
        status={displayStatus}
        onThreads={handleThreads}
        onStart={handleStart}
        startHeld={startHeld}
        startPending={startAndOpen.isPending}
        showStart={canOpenForm}
      />

      <SummaryChipStrip
        utilityName={effective.org_name ?? null}
        dueDate={effective.due_date}
        today={today}
        originLabel={t(`field-work:myWork.originLabels.${effective.origin}`)}
        pendingCount={pendingChangeRequestCount(effective, changeRequests)}
      />

      {pendingKind !== null ? (
        <div>
          {/* An assignment set carries only a removal or a replacement. */}
          <p className="text-sm font-medium text-on-surface">
            {pendingKind === "removal"
              ? t("field-work:myWork.pendingReview.removal")
              : t("field-work:myWork.pendingReview.replacement")}
          </p>
          {/* Presentation only: the set rides the open assignment until the
              utility decides it (an accepted removal cancels the work; a
              rejected one leaves the test due), which is why the status and
              Start & open above stay exactly as they are. */}
          <p className="mt-0.5 text-sm text-muted">
            {t("field-work:myWork.pendingReview.explainer")}
          </p>
        </div>
      ) : null}

      {showStaleNote ? (
        <p className="text-center text-sm text-muted">{t("field-work:sync.offline")}</p>
      ) : null}

      {unresolvedRejection !== null && rejectionDetail !== null ? (
        <CureBanner rejection={unresolvedRejection} detail={rejectionDetail} />
      ) : null}

      {canOpenForm ? (
        <BeforeYouStartCard
          isDeviceTest={isDeviceTest}
          kits={kits}
          selectedKitId={selectedKitId}
          onKitChange={setPickedKitId}
          kitVerdicts={kitVerdicts}
          kitsLoading={kitsQuery.isLoading}
          verdictsLoading={readiness.isLoading || offlineStartUnread}
          gateMounted={gateMounted}
          verdicts={verdicts}
          refusal={refusal}
          offline={offlineStart}
          today={today}
          acknowledged={acknowledged}
          onAcknowledgedChange={setAcknowledged}
          onStart={handleStart}
          pending={startAndOpen.isPending}
          draftNote={hasDraftInProgress(draft)}
        >
          {gateSubject(target, effective)}
        </BeforeYouStartCard>
      ) : null}

      {target ? (
        <TargetCard target={target} />
      ) : (
        <Card>
          {/* The bundle carries no target row for an assignment past
              `in_progress` (the sync query selects open ones only), so this arm
              is what a submitted or completed assignment renders — the
              post-submit landing. The denormalized labels ride the assignment
              itself, which is why they can name the premise here when the
              target lookup cannot; the uuid stays as the last-resort fallback
              rather than the default. */}
          {isReceiptStatus ? (
            <div className="mb-4">
              {/* The receipt says what happened and where to go next. Its date
                  is the assignment's own updated_at: the settle transition is
                  the row's last write, and the wire carries no separate
                  submitted-at to read. */}
              <p className="text-sm font-semibold text-on-surface">
                {t(
                  displayStatus === "submitted"
                    ? "field-work:detail.receipt.submitted"
                    : "field-work:detail.receipt.completed",
                  { date: formatDateTime(effective.updated_at) },
                )}
              </p>
              <div className="mt-3 flex gap-4">
                <Link
                  to="/my-work"
                  search={{ view: "cards" }}
                  className="text-sm font-medium text-primary hover:text-primary-hover max-sm:inline-flex max-sm:min-h-11 max-sm:min-w-11 max-sm:items-center"
                >
                  {t("field-work:detail.receipt.myWork")}
                </Link>
                <Link
                  to="/"
                  className="text-sm font-medium text-primary hover:text-primary-hover max-sm:inline-flex max-sm:min-h-11 max-sm:min-w-11 max-sm:items-center"
                >
                  {t("field-work:detail.receipt.home")}
                </Link>
              </div>
            </div>
          ) : null}
          <p className="text-xs font-semibold uppercase text-muted">
            {t("field-work:detail.facilityRef")}
          </p>
          <p className="mt-0.5 text-sm text-muted">
            {effective.facility_name ?? effective.facility_id}
          </p>
          {effective.device_installation_id ? (
            <>
              <p className="mt-3 text-xs font-semibold uppercase text-muted">
                {t("field-work:detail.installationRef")}
              </p>
              <p className="mt-0.5 text-sm text-muted">
                {effective.device_serial_number ?? effective.device_installation_id}
              </p>
            </>
          ) : null}
        </Card>
      )}

      <div ref={threadsRef} className="scroll-mt-20 sm:scroll-mt-36">
        <AssignmentThreadsCard assignmentId={assignmentId} />
      </div>

      {outboxRows.length > 0 ? (
        <Card title={t("field-work:detail.submissions")}>
          {outboxRows.map((row) => (
            <div
              key={row.clientSubmissionId}
              className="flex items-center justify-between gap-3 py-1.5"
            >
              <span className="text-sm text-muted">
                {formatDateTime(new Date(row.createdAt).toISOString())}
              </span>
              <SyncStatusChip
                state={row.state}
                onRetry={() => {
                  void handleRetry(row);
                }}
                lastError={row.lastError}
                blockedRequirements={row.blockedRequirements}
                requests={outboxRowRequests(row, rowAttachments[row.clientSubmissionId] ?? [])}
                utilityName={effective.org_name ?? ""}
                proposals={outboxRowProposals(row, effective.facility_id)}
              />
            </div>
          ))}
          {hasRevoked ? (
            <p className="mt-2 text-sm text-danger">{t("field-work:sync.revokedBody")}</p>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
